/**
 * Ricerca anti-doppione per la creazione guidata dal pannello email.
 *
 * Separato da `companyProspect.ts` perche' qui si tocca Supabase: le funzioni
 * pure restano testabili in ambiente node, come vuole la configurazione vitest
 * del progetto.
 */
import { supabase } from '../supabase';
import { findCompanyByDomain } from '../utils/companyDomainMatch';
import type { ExistingMatches } from './companyProspect';

/** Verifica se il collegamento persona-azienda esiste gia`, per non duplicarlo. */
export async function linkExists(personId: string, companyId: string): Promise<boolean> {
  const { data } = await supabase
    .from('client_relationships')
    .select('id')
    .eq('relationship_type', 'contatto')
    .eq('source_client_id', personId)
    .eq('target_client_id', companyId)
    .eq('is_active', true)
    .limit(1);
  return !!data && data.length > 0;
}

export async function findExistingEntities(args: {
  organizationId: string;
  domain: string | null;
  vatNumber: string | null;
  personEmail: string | null;
}): Promise<ExistingMatches> {
  const { organizationId, domain, vatNumber, personEmail } = args;

  const byVat = async () => {
    if (!vatNumber) return null;
    const { data } = await supabase
      .from('clients')
      .select('id, business_name')
      .eq('organization_id', organizationId)
      .eq('entity_type', 'company')
      .eq('vat_number', vatNumber)
      .limit(1);
    return data && data.length > 0 ? { id: data[0].id, business_name: data[0].business_name } : null;
  };

  const byEmail = async () => {
    if (!personEmail) return null;
    const { data } = await supabase
      .from('clients')
      .select('id, business_name')
      .eq('organization_id', organizationId)
      .eq('entity_type', 'individual')
      .ilike('email', personEmail)
      .limit(1);
    return data && data.length > 0 ? { id: data[0].id, business_name: data[0].business_name } : null;
  };

  const [vatCompany, domainCompany, person] = await Promise.all([
    byVat(),
    domain ? findCompanyByDomain(organizationId, domain) : Promise.resolve(null),
    byEmail(),
  ]);

  // La partita IVA e` identificativa, il dominio no: se divergono vince la partita IVA.
  const company = vatCompany || domainCompany;
  const companyMatchedBy: ExistingMatches['companyMatchedBy'] =
    vatCompany ? 'vat' : domainCompany ? 'domain' : null;

  return { company, companyMatchedBy, person };
}
